import React from 'react';
import { Server, ChevronRight } from 'lucide-react';
import { LocationData, InstalledChiller } from '../types';
import { CHILLER_TYPES, HEAT_REJECTION_LEVELS, CRAH_LEVELS } from '../constants';
import { coolingChainStages, climateFactorFor } from '../hooks/useCoolingManagement';

interface ChillerManagementModalProps {
  currentLocationData: LocationData;
  buyChiller: (locationId: string, chillerTypeId: string) => void;
  upgradeChiller: (locationId: string, installChillerId: string) => void;
  installHeatRejection: (locationId: string) => void;
  upgradeHeatRejection: (locationId: string) => void;
  installCrah: (locationId: string) => void;
  upgradeCrah: (locationId: string) => void;
  formatCurrency: (value: number) => string;
  currentLocation: string;
  isOpen: boolean;
  onClose: () => void;
  onGoToServers?: () => void; // closes this modal so the player can rack servers (setup flow)
}

type StageStatus = 'ok' | 'bottleneck' | 'missing';

// Segmented level-progress track
const LevelTrack: React.FC<{ current: number; max: number }> = ({ current, max }) => (
  <div className="flex items-center gap-1 my-2">
    {Array.from({ length: max }).map((_, i) => (
      <span key={i} className={`h-1.5 flex-1 rounded ${i < current ? 'bg-emerald-500' : 'bg-gray-600'}`} />
    ))}
    <span className="text-[11px] text-gray-400 ml-1 whitespace-nowrap">Lv {current}/{max}</span>
  </div>
);

// Next-level stat preview (current → next)
const NextLevelStats: React.FC<{ rows: { label: string; from: string; to: string }[] }> = ({ rows }) => (
  <div className="text-xs bg-gray-800/70 rounded px-2 py-1.5 mb-2">
    <div className="text-gray-400 mb-0.5">Next level:</div>
    {rows.map(r => (
      <div key={r.label} className="flex justify-between">
        <span className="text-gray-400">{r.label}</span>
        <span className="text-gray-300">{r.from} → <span className="text-emerald-300 font-medium">{r.to}</span></span>
      </div>
    ))}
  </div>
);

const ChillerManagementModal: React.FC<ChillerManagementModalProps> = ({
  currentLocationData,
  buyChiller,
  upgradeChiller,
  installHeatRejection,
  upgradeHeatRejection,
  installCrah,
  upgradeCrah,
  formatCurrency,
  currentLocation,
  isOpen,
  onClose,
  onGoToServers,
}) => {
  if (!isOpen) return null;

  const loc = currentLocationData;
  const climate = climateFactorFor(currentLocation);
  const stages = coolingChainStages(loc, currentLocation);
  const { heatRejectionKW, chillerKW, crahKW, effectiveKW } = stages;

  const hasHeatRej = !!loc.heatRejection?.installed;
  const hasChiller = loc.chillers.length > 0;
  const hasCrah = !!loc.crah?.installed;
  const coolingReady = hasHeatRej && hasChiller && hasCrah;

  // Determine the bottleneck stage (smallest non-zero capacity among installed stages)
  const stageCaps: { key: string; cap: number; present: boolean }[] = [
    { key: 'heat', cap: heatRejectionKW, present: hasHeatRej },
    { key: 'chiller', cap: chillerKW, present: hasChiller },
    { key: 'crah', cap: crahKW, present: hasCrah },
  ];
  const bottleneckKey = stageCaps.filter(s => s.present).sort((a, b) => a.cap - b.cap)[0]?.key;

  const statusFor = (present: boolean, key: string): StageStatus =>
    !present ? 'missing' : (key === bottleneckKey && effectiveKW > 0 ? 'bottleneck' : 'ok');

  const statusColor = (s: StageStatus) =>
    s === 'ok' ? 'border-emerald-500 bg-emerald-900/20 text-emerald-300'
    : s === 'bottleneck' ? 'border-amber-500 bg-amber-900/20 text-amber-300'
    : 'border-gray-600 bg-gray-800 text-gray-500';

  const getChillerType = (typeId: string) => CHILLER_TYPES.find(t => t.id === typeId);
  const getNextChillerLevel = (typeId: string, level: number) => getChillerType(typeId)?.levels.find(l => l.level === level + 1);
  const heatRejNext = loc.heatRejection ? HEAT_REJECTION_LEVELS[loc.heatRejection.level + 1] : null;
  const crahNext = loc.crah ? CRAH_LEVELS[loc.crah.level + 1] : null;

  const FlowBox = ({ label, sub, status }: { label: string; sub: string; status: StageStatus }) => (
    <div className={`flex-1 rounded-lg border px-3 py-2 text-center ${statusColor(status)}`}>
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-xs opacity-80">{sub}</div>
    </div>
  );

  const Arrow = () => <div className="text-gray-500 px-1 self-center">→</div>;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 text-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold">Cooling Chain — {loc.name}</h2>
            <p className="text-sm text-gray-400">
              Climate factor ×{climate.toFixed(2)} {climate >= 1 ? '(free-cooling bonus)' : '(hot climate penalty)'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Flow diagram */}
        <div className="flex items-stretch gap-1 mb-4">
          <FlowBox label="Heat Rejection" sub={hasHeatRej ? `${Math.round(heatRejectionKW)} kW` : 'Not installed'} status={statusFor(hasHeatRej, 'heat')} />
          <Arrow />
          <FlowBox label="Chiller Plant" sub={hasChiller ? `${Math.round(chillerKW)} kW` : 'No chillers'} status={statusFor(hasChiller, 'chiller')} />
          <Arrow />
          <FlowBox label="CRAH Air" sub={hasCrah ? `${Math.round(crahKW)} kW` : 'Not installed'} status={statusFor(hasCrah, 'crah')} />
          <Arrow />
          <FlowBox label="Racks" sub={`${Math.round(effectiveKW)} kW cooled`} status={effectiveKW > 0 ? 'ok' : 'missing'} />
        </div>

        {/* Effective capacity summary */}
        <div className="mb-6 bg-gray-700 rounded-lg p-4">
          <div className="flex justify-between mb-2">
            <span className="font-semibold">Delivered cooling (chain bottleneck)</span>
            <span className="font-bold text-lg">{Math.round(effectiveKW)} kW</span>
          </div>
          <div className="w-full bg-gray-600 rounded-full h-2.5">
            <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${effectiveKW > 0 ? Math.min(100, (loc.usedCoolingCapacityKW / effectiveKW) * 100) : 0}%` }} />
          </div>
          <div className="flex justify-between text-sm mt-1 text-gray-300">
            <span>Used: {Math.round(loc.usedCoolingCapacityKW)} kW</span>
            <span>Free: {Math.round(Math.max(0, effectiveKW - loc.usedCoolingCapacityKW))} kW</span>
          </div>
          {effectiveKW === 0 && (
            <p className="text-amber-400 text-xs mt-2">⚠ Cooling chain incomplete — install all three stages (Heat Rejection → Chiller → CRAH) to cool racks.</p>
          )}
        </div>

        {/* Stage 1 — Heat Rejection */}
        <div className="mb-5">
          <h3 className="text-lg font-semibold mb-2">1 · Heat Rejection <span className="text-xs text-gray-400">(cooling towers / dry coolers)</span></h3>
          {hasHeatRej ? (
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-sm">Level {loc.heatRejection!.level} · {loc.heatRejection!.capacityKW} kW nameplate → <span className="text-emerald-300">{Math.round(heatRejectionKW)} kW</span> at this climate</p>
              <p className="text-gray-400 text-sm">Power draw: {loc.heatRejection!.powerDrawKW} kW</p>
              <LevelTrack current={loc.heatRejection!.level} max={HEAT_REJECTION_LEVELS.length - 1} />
              {heatRejNext ? (
                <>
                  <NextLevelStats rows={[
                    { label: 'Capacity', from: `${loc.heatRejection!.capacityKW} kW`, to: `${heatRejNext.capacityKW} kW (×${climate.toFixed(2)} = ${Math.round(heatRejNext.capacityKW * climate)})` },
                    { label: 'Power draw', from: `${loc.heatRejection!.powerDrawKW} kW`, to: `${heatRejNext.powerDrawKW} kW` },
                  ]} />
                  <button onClick={() => upgradeHeatRejection(currentLocation)} className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-semibold">Upgrade to Level {heatRejNext.level} · ${formatCurrency(heatRejNext.cost)}</button>
                </>
              ) : <p className="text-xs text-emerald-400">Maximum level reached — fully upgraded.</p>}
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
              <div className="text-sm text-gray-300">
                Dumps server heat outdoors. Required before chillers. Cold climates reject heat more efficiently.
                <div className="text-gray-500 text-xs mt-1">{HEAT_REJECTION_LEVELS.length - 1} levels · L1: {HEAT_REJECTION_LEVELS[1].capacityKW} kW → max L{HEAT_REJECTION_LEVELS.length - 1}: {HEAT_REJECTION_LEVELS[HEAT_REJECTION_LEVELS.length - 1].capacityKW} kW (before ×{climate.toFixed(2)} climate)</div>
              </div>
              <button onClick={() => installHeatRejection(currentLocation)} className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-sm whitespace-nowrap">Install · ${formatCurrency(HEAT_REJECTION_LEVELS[1].cost)}</button>
            </div>
          )}
        </div>

        {/* Stage 2 — Chillers */}
        <div className="mb-5">
          <h3 className="text-lg font-semibold mb-2">2 · Chiller Plant</h3>
          {!hasHeatRej && <p className="text-amber-400 text-xs mb-2">Install heat rejection first.</p>}
          {loc.chillers.length > 0 && (
            <div className="space-y-2 mb-3">
              {loc.chillers.map((chiller: InstalledChiller) => {
                const type = getChillerType(chiller.typeId);
                const next = getNextChillerLevel(chiller.typeId, chiller.level);
                const lvl = type?.levels.find(l => l.level === chiller.level);
                const maxLvl = type ? type.levels.length : 0;
                return (
                  <div key={chiller.id} className="bg-gray-700 rounded-lg p-3 flex justify-between items-center gap-3">
                    <div className="text-sm flex-1">
                      <p className="font-medium">{type?.name} <span className="text-gray-400">(Lv {chiller.level}/{maxLvl})</span></p>
                      <p className="text-gray-400">{lvl?.coolingOutputKW} kW · {lvl?.powerConsumptionKW} kW power</p>
                      {next && <p className="text-emerald-300 text-xs mt-0.5">Next: {next.coolingOutputKW} kW (+{(next.coolingOutputKW - (lvl?.coolingOutputKW ?? 0))}) · {next.powerConsumptionKW} kW power</p>}
                    </div>
                    {next
                      ? <button onClick={() => upgradeChiller(currentLocation, chiller.id)} className="bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded text-sm whitespace-nowrap">Upgrade Lv {next.level} · ${formatCurrency(next.cost)}</button>
                      : <span className="text-gray-400 text-sm">Max</span>}
                  </div>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {CHILLER_TYPES.map(type => (
              <div key={type.id} className="bg-gray-700 rounded-lg p-3">
                <p className="font-medium text-sm">{type.name}</p>
                <p className="text-xs text-gray-400">{type.levels[0].coolingOutputKW} kW · {type.levels[0].powerConsumptionKW} kW power</p>
                <p className="text-xs text-gray-500 mb-2">{type.levels.length} levels · max {type.levels[type.levels.length - 1].coolingOutputKW} kW</p>
                <button
                  onClick={hasHeatRej ? () => buyChiller(currentLocation, type.id) : undefined}
                  disabled={!hasHeatRej}
                  className={`w-full py-1.5 rounded text-sm ${hasHeatRej ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 opacity-60 cursor-not-allowed'}`}
                >
                  {hasHeatRej ? `Buy · $${formatCurrency(type.levels[0].cost)}` : 'Needs heat rejection'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Stage 3 — CRAH */}
        <div>
          <h3 className="text-lg font-semibold mb-2">3 · CRAH Air Handlers <span className="text-xs text-gray-400">(delivers cold air to the floor)</span></h3>
          {hasCrah ? (
            <div className="bg-gray-700 rounded-lg p-4">
              <p className="text-sm">Level {loc.crah!.level} · {loc.crah!.capacityKW} kW air delivery</p>
              <p className="text-gray-400 text-sm">Power draw: {loc.crah!.powerDrawKW} kW</p>
              <LevelTrack current={loc.crah!.level} max={CRAH_LEVELS.length - 1} />
              {crahNext ? (
                <>
                  <NextLevelStats rows={[
                    { label: 'Air delivery', from: `${loc.crah!.capacityKW} kW`, to: `${crahNext.capacityKW} kW (+${crahNext.capacityKW - loc.crah!.capacityKW})` },
                    { label: 'Power draw', from: `${loc.crah!.powerDrawKW} kW`, to: `${crahNext.powerDrawKW} kW` },
                  ]} />
                  <button onClick={() => upgradeCrah(currentLocation)} className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-semibold">Upgrade to Level {crahNext.level} · ${formatCurrency(crahNext.cost)}</button>
                </>
              ) : <p className="text-xs text-emerald-400">Maximum level reached — fully upgraded.</p>}
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
              <div className="text-sm text-gray-300">
                Final stage — pushes chilled air into the racks. Requires at least one chiller.
                <div className="text-gray-500 text-xs mt-1">{CRAH_LEVELS.length - 1} levels · L1: {CRAH_LEVELS[1].capacityKW} kW → max L{CRAH_LEVELS.length - 1}: {CRAH_LEVELS[CRAH_LEVELS.length - 1].capacityKW} kW</div>
              </div>
              <button
                onClick={hasChiller ? () => installCrah(currentLocation) : undefined}
                disabled={!hasChiller}
                className={`px-4 py-2 rounded text-sm whitespace-nowrap ${hasChiller ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 opacity-60 cursor-not-allowed'}`}
              >
                {hasChiller ? `Install · $${formatCurrency(CRAH_LEVELS[1].cost)}` : 'Needs a chiller'}
              </button>
            </div>
          )}
        </div>

        {/* Next-step footer — chains the setup flow toward racking servers */}
        {onGoToServers && (
          <div className="mt-6 border-t border-gray-700 pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm">
              {coolingReady ? (
                <span className="text-emerald-400 font-medium">✓ Cooling ready — time to rack your servers.</span>
              ) : (
                <span className="text-gray-400">Install all three cooling stages, then head to the floor to rack servers.</span>
              )}
            </div>
            <button
              onClick={onGoToServers}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                coolingReady
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30'
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              }`}
            >
              <Server className="w-4 h-4" />
              {coolingReady ? 'Rack servers' : 'Go to the floor'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChillerManagementModal;
